import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Api from 'Api';
import Reports from 'Api/Reports';
import { pollCombinedReportAiJob, pollPendingCombinedReportAiJob } from 'utils/weeklyreportsv2/pollCombinedReportAiJob';
import {
    COMBINED_REPORT_AI_JOB_FINISHED,
    COMBINED_REPORT_AI_JOB_QUEUED,
    clearPendingCombinedReportAiJob,
    listPendingCombinedReportAiJobs,
    pendingWatchKey,
    type PendingCombinedReportAiJob,
} from 'utils/weeklyreportsv2/pendingCombinedReportAiJob';

/** In-flight watches keyed by messageId/jobId — avoids duplicate polls, allows restart after completion. */
const activeWatches = new Map<string, Promise<void>>();

type PlatformNotificationRow = {
    notificationId?: number | string;
    notificationTitle?: string;
    notificationMessage?: string;
    notificationStatus?: number;
    notifiedAt?: string;
};

async function notifyBellLive(
    teacherId: string | number | null | undefined,
    watchStartedAt?: number,
): Promise<void> {
    let toastTitle = 'Your AI report summary is ready';

    if (teacherId != null && teacherId !== '') {
        // Bell row is written just before job=completed — brief delay then retry once if needed.
        for (const delayMs of [500, 1500]) {
            await new Promise((r) => setTimeout(r, delayMs));
            try {
                const received = await Api.getNotifications(teacherId, 5, 0);
                const list: PlatformNotificationRow[] = Array.isArray(received?.notifications)
                    ? received.notifications
                    : [];
                const newest = list.find((n) => {
                    if (!String(n?.notificationTitle || '').toLowerCase().includes('ai report')) return false;
                    if (!watchStartedAt || !n?.notifiedAt) return true;
                    const notifiedMs = new Date(String(n.notifiedAt)).getTime();
                    return Number.isFinite(notifiedMs) && notifiedMs >= watchStartedAt - 5000;
                });

                if (newest?.notificationTitle) {
                    toastTitle = newest.notificationTitle;
                    break;
                }
            } catch {
                /* retry or fall back to generic toast */
            }
        }
    }

    toast.success(toastTitle);
}

function finishWatch(
    pending: PendingCombinedReportAiJob,
    dispatch: (action: unknown) => void,
    teacherId: string | number | null | undefined,
): void {
    const watchKey = pendingWatchKey(pending);
    if (!watchKey) return;

    const existing = activeWatches.get(watchKey);
    if (existing) return;

    const watchPromise = finishWatchInner(pending, dispatch, teacherId).finally(() => {
        activeWatches.delete(watchKey);
    });
    activeWatches.set(watchKey, watchPromise);
}

async function finishWatchInner(
    pending: PendingCombinedReportAiJob,
    dispatch: (action: unknown) => void,
    teacherId: string | number | null | undefined,
): Promise<void> {
    const { jobId, schoolId, reportId, startedAt } = pending;

    try {
        const job = jobId
            ? await pollCombinedReportAiJob(jobId)
            : await pollPendingCombinedReportAiJob({
                  schoolId,
                  reportId,
                  messageId: pending.messageId,
                  watchStartedAt: startedAt,
              });
        const status = String(job?.status || '').toLowerCase();
        const completedJobId = String(job?.jobId || jobId || '');

        try {
            if (completedJobId) await Reports.ackCombinedReportAi(completedJobId);
        } catch {
            /* non-blocking */
        }
        clearPendingCombinedReportAiJob(schoolId, reportId);

        if (status === 'completed') {
            await notifyBellLive(teacherId, startedAt);
            // Sync badge/list from API (replaces any optimistic prepend with server truth).
            window.dispatchEvent(new CustomEvent('lsgo:notifications-refresh'));
        } else {
            toast.error((typeof job?.error === 'string' && job.error) || 'AI report summary failed');
        }

        window.dispatchEvent(
            new CustomEvent(COMBINED_REPORT_AI_JOB_FINISHED, {
                detail: {
                    jobId: completedJobId,
                    schoolId,
                    reportId,
                    status,
                    error: typeof job?.error === 'string' ? job.error : undefined,
                },
            }),
        );
    } catch (err: any) {
        clearPendingCombinedReportAiJob(schoolId, reportId);
        toast.error(err?.response?.data?.message || err?.message || 'Failed to generate AI summary');
        try {
            if (jobId) await Reports.ackCombinedReportAi(jobId);
        } catch {
            /* non-blocking */
        }
        window.dispatchEvent(
            new CustomEvent(COMBINED_REPORT_AI_JOB_FINISHED, {
                detail: {
                    jobId,
                    schoolId,
                    reportId,
                    status: 'failed',
                    error: err?.message,
                },
            }),
        );
    }
}

/**
 * Platform-wide watcher mounted next to ToastifyNotification in AdminLayout.
 * Polls pending combined report AI jobs while the teacher is anywhere on the
 * authenticated platform — not only on the report page.
 */
const CombinedReportAiJobWatcher = () => {
    const dispatch = useDispatch();
    const teacherId = useSelector((state: any) => state.auth?.teacher?.id);
    const teacherIdRef = useRef(teacherId);
    teacherIdRef.current = teacherId;

    useEffect(() => {
        const onQueued = (event: Event) => {
            const detail = (event as CustomEvent<PendingCombinedReportAiJob>).detail;
            if (detail?.jobId || detail?.messageId) void finishWatch(detail, dispatch, teacherIdRef.current);
        };

        listPendingCombinedReportAiJobs().forEach((pending) => {
            void finishWatch(pending, dispatch, teacherIdRef.current);
        });

        window.addEventListener(COMBINED_REPORT_AI_JOB_QUEUED, onQueued);
        return () => {
            window.removeEventListener(COMBINED_REPORT_AI_JOB_QUEUED, onQueued);
        };
    }, [dispatch]);

    return null;
};

export default CombinedReportAiJobWatcher;
